import type { RefObject } from 'react';
import { useEffect, useState } from 'react';

/**
 * Хук для разделения клика и движения с зажатым курсором вне элемента, на который указывает ref
 * @param ref - ссылка на элемент, вне которого отслеживается клик и движение с зажатым курсором
 * @param isMobile - флаг, является ли устройство мобильным
 * @param callback - коллбэк, который выполнится при клике, но не выполнится, если было движение с зажатым курсором
 */

interface IUseOnClickEventsOutside {
  ref: RefObject<HTMLElement>;
  isMobile: boolean;
  callback: () => void;
}

export const useOnClickEventsOutside = ({
  ref,
  isMobile,
  callback,
}: IUseOnClickEventsOutside) => {
  const [startPos, setStartPos] = useState<number[]>([]);
  const [isMove, setIsMove] = useState<boolean>(false);

  useEffect(() => {
    const listener = (event) => {
      const isOutside = ref.current && !ref.current.contains(event.target);
      if (!isOutside) return;

      switch (event.type) {
        case 'mousedown':
          setStartPos([event.clientX, event.clientY]);
          setIsMove(false);
          break;
        case 'touchstart':
          setStartPos([event.touches[0].clientX, event.touches[0].clientY]);
          setIsMove(false);
          break;

        case 'mousemove':
          if (startPos[0] !== event.clientX || startPos[1] !== event.clientY)
            setIsMove(true);
          break;
        case 'touchmove':
          if (
            startPos[0] !== event.touches[0].clientX ||
            startPos[1] !== event.touches[0].clientY
          )
            setIsMove(true);
          break;

        case 'mouseup':
        case 'touchend':
          if (!isMove) {
            callback();
          }
          break;

        default:
      }
    };

    if (isMobile) {
      document.addEventListener('touchstart', listener);
      document.addEventListener('touchmove', listener);
      document.addEventListener('touchend', listener);
    } else {
      document.addEventListener('mousedown', listener);
      document.addEventListener('mousemove', listener);
      document.addEventListener('mouseup', listener);
    }

    return () => {
      if (isMobile) {
        document.removeEventListener('touchstart', listener);
        document.removeEventListener('touchmove', listener);
        document.removeEventListener('touchend', listener);
      } else {
        document.removeEventListener('mousedown', listener);
        document.removeEventListener('mousemove', listener);
        document.removeEventListener('mouseup', listener);
      }
    };
  }, [ref, callback, isMobile, isMove, startPos]);
};
